import { App, Route, UniversalAppState } from "@pimo/pimo-app-builder";
import {
  DefaultOverlayPartialAppState,
  PimoFormDialog,
  PMPopUp,
} from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { ResourceLockPlugin } from "@pimo/resource-lock-plugin";
import { generatePath } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { Project } from "vmd-types";
import {
  CHARACTER_LIMITATIONS,
  getFieldValidation,
  validateBasicProjectForm,
} from "vmd-utils";

import { TestAppState } from "../app";
import { APP_ROUTES } from "../constants";
import { fetchDashboard } from "../helpers/fetch-dashboard";
import { fetchEntityConfiguration } from "../helpers/fetch-entity-configurations";
import { fetchOverlay } from "../helpers/fetch-overlay";
import { fetchOverview } from "../helpers/fetch-overview";
import {
  createProject,
  fetchProject,
  updateProject,
} from "../helpers/fetch-project";
import { fetchSettings } from "../helpers/fetch-settings";
import { showSnackbar } from "../helpers/show-snackbar";

const STYLE_TEXT = {
  "& .MuiInputBase-input": {
    fontSize: "14px",
  },
  // multiline textarea
  "& .MuiInputBase-inputMultiline, & textarea": {
    fontSize: "14px",
  },
  // label size (optional)
  "& .MuiInputLabel-root": {
    fontSize: "14px",
  },
  fontSize: "14px",
  width: "100%",
  borderRadius: "6px",
  height: "45px",
};

export async function buildCreateProjectFormRoute(
  app: App<TestAppState>,
  parentRoute: Route<"", TestAppState>,
  isFromOverview: boolean
) {
  const dialog = app.createView({
    name: "add-project",
  });
  let projectId: string | undefined;
  const projectForm = new PimoFormDialog<Project>();

  const form = dialog.addComponent({ component: projectForm });

  registerFormEvents();

  function registerFormEvents() {
    form.on("submit", async ({ payload: payload = {} as Project }) => {
      if (payload.flag !== "create") {
        await updateProject(payload.id, payload, false);
      } else {
        await createProject(payload);
      }

      const [overlay, overview, dashboard, currentReport, settings] =
        await Promise.all([
          await fetchOverlay(),
          await fetchOverview(),
          await fetchDashboard(),
          await fetchProject({
            id: app.getAppState().currentReport?.project?.id ?? "",
          }),
          fetchSettings(),
        ]);

      app?.patchAppState({
        overlay,
        overview,
        dashboard,
        currentReport,
        settings: { ...app.getAppState().settings, ...settings },
      });

      showSnackbar(
        app as unknown as App<
          UniversalAppState & DefaultOverlayPartialAppState
        >,
        "All changes have been saved.",
        "success"
      );

      closeEditForm();
    });

    form.on("cancel", () => closeEditForm());
    form.on("cancel-with-changes", () =>
      app.patchAppState({ showEditFormConfirmationDialog: true })
    );
    form.on("close", () => closeEditForm());
  }
  function closeEditForm() {
    const resourceLockPlugin =
      app.getPluginByName<ResourceLockPlugin<TestAppState>>(
        "ResourceLockPlugin"
      );

    app.patchAppState({
      editForm: {},
    });

    resourceLockPlugin?.unlock(`project-${projectId}`);

    isFromOverview
      ? app.navigate(APP_ROUTES.overview)
      : app.navigate(
          generatePath(APP_ROUTES.report, {
            id: projectId ?? "",
            viewname: "dashboard",
          })
        );
  }

  const route = parentRoute.createChildRoute<"id" | "isFromOverview">({
    path: APP_ROUTES.projectDialog,
    view: dialog,
    isOverlayChild: true,
  });

  function createConfirmationDialog() {
    const confirmationDialog = dialog.addComponent({
      component: PMPopUp,
    });

    confirmationDialog.mapVisibility(
      ({ showEditFormConfirmationDialog }) =>
        showEditFormConfirmationDialog === true
    );

    confirmationDialog.mapState(() => {
      const language = app.getAppState().language || "en";
      return {
        title:
          getLocalizedMessage(language, "dialog.title.unsavedChanges", {
            entity: "form",
          }) || "Unsaved Changes",
        description:
          getLocalizedMessage(language, "dialog.message.unsavedChanges", {
            entity: "form",
          }) ||
          "You have made changes that have not been saved. If you close the settings, these changes will be lost.",
        maxWidth: "sm",

        cancelButtonText:
          getLocalizedMessage(language, "dialog.button.cancel") || "Cancel",
        cancelButtonColor: "white",
        confirmationButtonColor: "blue",
        confirmationButtonText:
          getLocalizedMessage(language, "dialog.button.continue") || "Continue",

        cancelButtonFireEvent: () => closeConfirmationDialog(),
        confirmationButtonFireEvent: () => {
          closeConfirmationDialog();
          closeEditForm();
        },
        onClose: () => closeConfirmationDialog(),
      };
    });

    function closeConfirmationDialog() {
      app.patchAppState({
        showEditFormConfirmationDialog: false,
      });
    }
  }
  createConfirmationDialog();

  route.on("load", async (payload) => {
    isFromOverview = payload.payload?.parameters?.isFromOverview === "true";
    projectId = payload.payload?.parameters?.id;
    const currentProject =
      projectId && projectId !== "new-project"
        ? await fetchProject({ id: projectId })
        : undefined;
    const isCreateMode = !currentProject?.project;
    const projectForForm =
      currentProject?.project ??
      ({
        id: uuidv4(),
        flag: "create",
        startDate: new Date().toISOString().split("T")[0],
        endDate: new Date().toISOString().split("T")[0],
        userCreatedProject: true,
      } as Project);
    const configuration = await fetchEntityConfiguration();

    app.patchAppState({
      currentReport: currentProject,
      showEditFormConfirmationDialog: false,
      editForm: {
        currentProject: projectForForm,
      },
      settings: {
        ...app.getAppState().settings,
        configuration: configuration,
      },
    });
    form.mapState((appState) => {
      return {
        ...(appState.editForm?.currentProject ?? projectForForm),
        ...{
          validateFormData: (data: Omit<Project, "id">) =>
            validateBasicProjectForm(data, configuration, {
              fieldRequired:
                getLocalizedMessage(
                  appState.language || "en",
                  "validationErrors.fieldRequired"
                ) || "This field is required",
              dateNotValid:
                getLocalizedMessage(
                  appState.language || "en",
                  "validationErrors.dateNotValid"
                ) || "This date is not valid",
              endDateAfterStartDate:
                getLocalizedMessage(
                  appState.language || "en",
                  "validationErrors.endDateAfterStartDate"
                ) || "End date should be after start date",
            }),
        },
        title: isCreateMode
          ? getLocalizedMessage(appState.language || "en", "createProject", {
              projectName:
                appState.settings?.configuration?.projectName ?? "Project",
            }) ||
            `Create ${
              appState.settings?.configuration?.projectName ?? "Project"
            }`
          : getLocalizedMessage(appState.language || "en", "editProject", {
              projectName:
                appState.settings?.configuration?.projectName ?? "Project",
            }) ||
            `Edit ${appState.settings?.configuration?.projectName ?? "Project"}`,
        inline: true,
        enableCloseButton: true,
        actions: {
          "save-and-back": { visible: false },
          "save-and-close": {
            visible: true,
            sx: {
              color: "white",
              backgroundColor: (theme) => theme.palette.primary.main,
              boxShadow: "none",
              "&.Mui-disabled": {
                backgroundColor: (theme) =>
                  theme.palette.action.disabledBackground,
                color: (theme) => theme.palette.action.disabled,
              },
            },
            label:
              getLocalizedMessage(appState.language || "en", "saveAndClose") ||
              "Save & Close",
          },
          "save-and-next": { visible: false },
          cancel: {
            label:
              getLocalizedMessage(appState.language || "en", "cancel") ||
              "Cancel",
          },
        },
      };
    });

    await projectForm.buildDynamicTabsAsynchronously(async () => {
      const appState = app.getAppState();
      const configuration = appState.settings?.configuration;
      const language = appState.language;

      const overallTab = projectForm.addFormTab({
        label: getLocalizedMessage(language || "en", "summary") || "Summary",
        id: "summary",
      });

      overallTab.addFormField({
        type: "text",
        label: `${getLocalizedMessage(language || "en", "name") || "Name"}${
          getFieldValidation(configuration, "name", "projectBasic") ? "*" : ""
        }`,
        multiline: false,
        name: "name",
        maxLength: {
          value: CHARACTER_LIMITATIONS["projectName"],
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.maxLength",
              { maxLength: CHARACTER_LIMITATIONS["projectName"] }
            ) ||
            `Must be ${CHARACTER_LIMITATIONS["projectName"]} characters or less`,
        },
        required: {
          value: true,
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required",
        },
        sx: STYLE_TEXT,
        uiOptions: {
          fieldSize: { xs: 6 },
        },
      });

      overallTab.addFormField({
        type: "text",
        label: `${
          getLocalizedMessage(language || "en", "customId") || "Custom ID"
        }${
          getFieldValidation(configuration, "customId", "projectBasic")
            ? "*"
            : ""
        }`,
        multiline: false,
        name: "customId",
        required: {
          value: !!getFieldValidation(
            configuration,
            "customId",
            "projectBasic"
          ),
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required",
        },
        sx: STYLE_TEXT,
        uiOptions: {
          fieldSize: { xs: 6 },
        },
      });

      overallTab.addFormField({
        type: "rich-text",
        label: `${
          getLocalizedMessage(language || "en", "description") || "Description"
        }${
          getFieldValidation(configuration, "description", "projectBasic")
            ? "*"
            : ""
        }`,
        multiline: true,
        name: "description",
        maxLength: {
          value: CHARACTER_LIMITATIONS["projectDescription"],
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.maxLength",
              { maxLength: CHARACTER_LIMITATIONS["projectDescription"] }
            ) ||
            `Must be ${CHARACTER_LIMITATIONS["projectDescription"]} characters or less`,
        },
        required: {
          value: true,
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required",
        },
        minRows: 3,
        sx: STYLE_TEXT,
        isVisible: () =>
          configuration?.fieldsDefinition
            ?.filter((field) => field.purpose === "projectBasic")
            ?.find((f) => f.fieldName === "description")?.enabled ?? false,
      });

      overallTab.addFormField({
        type: "text",
        label: `${getLocalizedMessage(language || "en", "owner") || "Owner"}${
          getFieldValidation(configuration, "owner", "projectBasic") ? "*" : ""
        }`,
        multiline: false,
        name: "owner",
        maxLength: {
          value: CHARACTER_LIMITATIONS["projectOwner"],
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.maxLength",
              { maxLength: CHARACTER_LIMITATIONS["projectOwner"] }
            ) ||
            `Must be ${CHARACTER_LIMITATIONS["projectOwner"]} characters or less`,
        },
        required: {
          value: !!getFieldValidation(configuration, "owner", "projectBasic"),
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required",
        },
        sx: STYLE_TEXT,
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        isVisible: () =>
          configuration?.fieldsDefinition
            ?.filter((field) => field.purpose === "projectBasic")
            ?.find((f) => f.fieldName === "owner")?.enabled ?? false,
      });

      overallTab.addFormField({
        type: "number",
        label: `${
          getLocalizedMessage(language || "en", "approvedBudget") ||
          "Approved Budget"
        } (€)${
          getFieldValidation(configuration, "approvedBudget", "projectBasic")
            ? "*"
            : ""
        }`,
        multiline: false,
        name: "approvedBudget",
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        maxLength: {
          value: CHARACTER_LIMITATIONS["approvedBudget"],
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.maxLength",
              {
                maxLength: CHARACTER_LIMITATIONS["approvedBudget"],
              }
            ) ||
            `Must be ${CHARACTER_LIMITATIONS["approvedBudget"]} characters or less`,
        },
        sx: STYLE_TEXT,
        isVisible: () =>
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "projectBasic")
              ?.find((f) => f.fieldName === "approvedBudget")?.enabled
          ),
      });

      overallTab.addFormField({
        type: "date",
        label: `${
          getLocalizedMessage(language || "en", "startDate") || "Start Date"
        }${
          getFieldValidation(configuration, "startDate", "projectBasic")
            ? "*"
            : ""
        }`,
        name: "startDate",
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        isVisible: () =>
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "projectBasic")
              ?.find((f) => f.fieldName === "startDate")?.enabled
          ),
      });
      overallTab.addFormField({
        type: "date",
        label: `${
          getLocalizedMessage(language || "en", "endDate") || "End Date"
        }${
          getFieldValidation(configuration, "endDate", "projectBasic")
            ? "*"
            : ""
        }`,
        name: "endDate",
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        isVisible: () =>
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "projectBasic")
              ?.find((f) => f.fieldName === "endDate")?.enabled
          ),
      });

      overallTab.addFormField({
        type: "dropdown",
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        label: `${
          configuration?.fieldsDefinition
            ?.filter((field) => field.purpose === "projectBasic")
            ?.find(
              (f) =>
                f.fieldName === "category" &&
                f.enabled === true &&
                (configuration?.categoryDefinition?.length ?? 0) > 0
            )?.label ??
          (getLocalizedMessage(language || "en", "category") || "Category")
        }${
          getFieldValidation(configuration, "category", "projectBasic")
            ? "*"
            : ""
        }`,
        name: `category`,
        sx: STYLE_TEXT,
        menuItemsSx: STYLE_TEXT,
        isVisible: () =>
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "projectBasic")
              ?.find((f) => f.fieldName === "category")?.enabled
          ),
        items:
          configuration?.categoryDefinition?.map((completion) => ({
            value: completion.name,
            label: completion.name,
          })) ?? [],
      });

      overallTab.addFormField({
        type: "text",
        label: `Audit Title${
          getFieldValidation(configuration, "freeTextFieldOne", "projectBasic")
            ? "*"
            : ""
        }`,
        multiline: false,
        name: "freeTextFieldOne",
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        sx: STYLE_TEXT,
        isVisible: () =>
          configuration?.fieldsDefinition
            ?.filter((field) => field.purpose === "projectBasic")
            ?.find((f) => f.fieldName === "freeTextFieldOne")?.enabled ?? false,
      });

      overallTab.addFormField({
        type: "dropdown",
        label: `${
          configuration?.fieldsDefinition
            ?.filter((field) => field.purpose === "projectBasic")
            ?.find(
              (f) =>
                f.fieldName === "secondCategory" &&
                f.enabled === true &&
                (configuration?.categoryDefinition?.length ?? 0) > 0
            )?.label ?? "Category"
        }${
          getFieldValidation(configuration, "secondCategory", "projectBasic")
            ? "*"
            : ""
        }`,
        name: `secondCategory`,
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        sx: STYLE_TEXT,
        menuItemsSx: STYLE_TEXT,
        isVisible: () =>
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "projectBasic")
              ?.find((f) => f.fieldName === "secondCategory")?.enabled
          ),
        items:
          configuration?.secondCategoryDefinition?.map((completion) => ({
            value: completion.name,
            label: completion.name,
          })) ?? [],
      });
    });
  });
}
